"use client";

import type { ReactNode } from "react";
import Link from "next/link";
import {
  Bell,
  CircleHelp,
  Coins,
  ImageIcon,
  LayoutDashboard,
  LogIn,
  LogOut,
  MessageCircle,
  Shirt,
  Ticket,
  User,
} from "lucide-react";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useCreditsContext } from "@/context/providers/CreditsProvider";
import { useMessageUnreadCount } from "@/hooks/useMessageUnreadCount";
import { isPendingAccountConfirmation } from "@/lib/auth/accountStatus";
import { cn } from "@/lib/utils";
import { trackNavigationClick } from "@/utils/analytics";

type AccountUser = {
  email?: string | null;
  email_confirmed_at?: string | null;
  is_anonymous?: boolean;
  new_email?: string | null;
  user_metadata?: Record<string, unknown>;
};

export function getAccountInitials(user: AccountUser): string | null {
  const metadataName = [
    user.user_metadata?.full_name,
    user.user_metadata?.name,
  ].find(value => typeof value === "string" && value.trim()) as
    | string
    | undefined;

  if (metadataName) {
    const nameParts = metadataName.trim().split(/\s+/);
    return nameParts.length > 1
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
      : metadataName.slice(0, 2).toUpperCase();
  }

  const emailName = user.email?.split("@")[0]?.trim();
  return emailName ? emailName.slice(0, 2).toUpperCase() : null;
}

function MenuCount({ count, label }: { count: number | null; label: string }) {
  if (count === null) return null;

  return (
    <span
      aria-label={`${count} ${label}`}
      className="ml-auto inline-flex min-w-6 items-center justify-center rounded-full bg-muted px-1.5 py-0.5 text-xs font-semibold tabular-nums text-foreground"
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

export function GlobalMenuLink({
  href,
  icon,
  children,
  count,
  countLabel,
  onClick,
  className,
}: {
  href: string;
  icon: ReactNode;
  children: ReactNode;
  count?: number | null;
  countLabel?: string;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <DropdownMenuItem
      asChild
      className={cn("min-h-11 cursor-pointer rounded-lg px-3", className)}
    >
      <Link
        href={href}
        onClick={() => {
          trackNavigationClick({
            destination: href,
            source: "navbar_menu",
            navigation_type: "internal",
          });
          onClick?.();
        }}
      >
        {icon}
        <span className="flex-1">{children}</span>
        {countLabel ? (
          <MenuCount count={count ?? null} label={countLabel} />
        ) : null}
      </Link>
    </DropdownMenuItem>
  );
}

export function GlobalMenuAction({
  icon,
  children,
  count,
  countLabel,
  onSelect,
  disabled = false,
  variant,
  className,
}: {
  icon: ReactNode;
  children: ReactNode;
  count?: number | null;
  countLabel?: string;
  onSelect: () => void;
  disabled?: boolean;
  variant?: React.ComponentProps<typeof DropdownMenuItem>["variant"];
  className?: string;
}) {
  return (
    <DropdownMenuItem
      onSelect={onSelect}
      disabled={disabled}
      variant={variant}
      className={cn("min-h-11 cursor-pointer rounded-lg px-3", className)}
    >
      {icon}
      <span className="flex-1">{children}</span>
      {countLabel ? (
        <MenuCount count={count ?? null} label={countLabel} />
      ) : null}
    </DropdownMenuItem>
  );
}

export function AuthenticatedGlobalMenuItems({
  showRequests,
  showAdmin,
  showMessageCount = true,
  notificationCount,
  onOpenNotifications,
}: {
  showRequests: boolean;
  showAdmin: boolean;
  showMessageCount?: boolean;
  notificationCount: number;
  onOpenNotifications: () => void;
}) {
  const { balance, isLoading: creditsLoading } = useCreditsContext();

  return (
    <>
      <GlobalMenuLink href="/account" icon={<Shirt aria-hidden />}>
        My Dresses
      </GlobalMenuLink>
      <GlobalMenuLink href="/gallery" icon={<ImageIcon aria-hidden />}>
        Gallery
      </GlobalMenuLink>
      {showMessageCount ? (
        <MessageMenuLinkWithCount />
      ) : (
        <GlobalMenuLink href="/messages" icon={<MessageCircle aria-hidden />}>
          Messages
        </GlobalMenuLink>
      )}
      <GlobalMenuLink
        href="/credits"
        icon={<Coins aria-hidden />}
        count={creditsLoading ? null : balance}
        countLabel="credits"
      >
        Credits
      </GlobalMenuLink>
      <GlobalMenuAction
        icon={<Bell aria-hidden />}
        count={notificationCount > 0 ? notificationCount : null}
        countLabel="unread notifications"
        onSelect={onOpenNotifications}
      >
        Notifications
      </GlobalMenuAction>
      <DropdownMenuSeparator />
      <GlobalMenuLink href="/#how-it-works" icon={<CircleHelp aria-hidden />}>
        How it works
      </GlobalMenuLink>
      {showRequests || showAdmin ? <DropdownMenuSeparator /> : null}
      {showRequests ? (
        <GlobalMenuLink href="/requests" icon={<Ticket aria-hidden />}>
          Requests
        </GlobalMenuLink>
      ) : null}
      {showAdmin ? (
        <GlobalMenuLink
          href="/admin/dashboard"
          icon={<LayoutDashboard aria-hidden />}
        >
          Admin
        </GlobalMenuLink>
      ) : null}
    </>
  );
}

function MessageMenuLinkWithCount() {
  const { unreadCount: messageCount, loading: messagesLoading } =
    useMessageUnreadCount();

  return (
    <GlobalMenuLink
      href="/messages"
      icon={<MessageCircle aria-hidden />}
      count={
        messagesLoading || messageCount.total <= 0 ? null : messageCount.total
      }
      countLabel="unread messages"
    >
      Messages
    </GlobalMenuLink>
  );
}

export function PublicGlobalMenuItems() {
  return (
    <>
      <GlobalMenuLink href="/gallery" icon={<ImageIcon aria-hidden />}>
        Gallery
      </GlobalMenuLink>
      <GlobalMenuLink href="/#how-it-works" icon={<CircleHelp aria-hidden />}>
        How it works
      </GlobalMenuLink>
    </>
  );
}

export function GlobalAccountMenuItems({
  user,
  onSignIn,
  onSignOut,
}: {
  user: AccountUser | null;
  onSignIn: () => void;
  onSignOut: () => void;
}) {
  if (!user) {
    return (
      <GlobalMenuAction icon={<LogIn aria-hidden />} onSelect={onSignIn}>
        Sign in
      </GlobalMenuAction>
    );
  }

  return (
    <>
      <DropdownMenuLabel className="min-w-0 px-3 py-2">
        <span className="block text-xs font-normal text-muted-foreground">
          Signed in as
        </span>
        <span className="block truncate text-sm font-medium text-foreground">
          {user.email ?? "Your account"}
        </span>
      </DropdownMenuLabel>
      <DropdownMenuSeparator />
      <GlobalMenuLink href="/account?tab=profile" icon={<User aria-hidden />}>
        Profile
      </GlobalMenuLink>
      {isPendingAccountConfirmation(user) ? (
        <GlobalMenuLink href="/credits" icon={<Coins aria-hidden />}>
          Confirm email
        </GlobalMenuLink>
      ) : (
        <GlobalMenuAction
          icon={<LogOut aria-hidden />}
          onSelect={onSignOut}
          variant="destructive"
        >
          Sign out
        </GlobalMenuAction>
      )}
    </>
  );
}
